import React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import {
    HeadsetMicRounded,
    HelpOutlineRounded,
    OpenInNewRounded,
    PhoneInTalkRounded,
    EmailOutlined,
    AccessTimeRounded,
    CheckCircleOutlineRounded,
} from "@mui/icons-material";

const _SupportInfo = () => {
    const theme = useTheme();

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                overflow: 'hidden',
                height: '100%',
            }}
        >
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                {/* Header with Icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.main',
                            color: '#fff',
                            boxShadow: '0 8px 16px rgba(45, 107, 226, 0.25)',
                        }}
                    >
                        <HeadsetMicRounded sx={{ fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            مرکز پشتیبانی جیم‌پین
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            پاسخگویی و پشتیبانی فنی سازمان
                        </Typography>
                    </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
                    لطفاً پیش از ثبت تیکت، نکات زیر را در نظر داشته باشید:
                </Typography>

                {/* FAQ Link Button */}
                <Button
                    fullWidth
                    variant="outlined"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://gympin.ir/faq"
                    startIcon={<HelpOutlineRounded />}
                    endIcon={<OpenInNewRounded sx={{ fontSize: 16 }} />}
                    sx={{
                        mb: 3,
                        py: 1.2,
                        borderRadius: 3,
                        fontWeight: 700,
                        borderWidth: 1.5,
                        borderColor: 'primary.main',
                        '&:hover': {
                            borderWidth: 1.5,
                            bgcolor: 'primary.50',
                        },
                    }}
                >
                    مشاهده سوالات متداول (FAQ)
                </Button>

                <Divider sx={{ my: 2.5, borderStyle: 'dashed' }} />

                {/* Key Points */}
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <CheckCircleOutlineRounded color="primary" sx={{ fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                            عنوان تیکت را مشخص و در شرح پیام، نام پرسنل، شماره همراه یا کد فاکتور مرتبط را درج نمایید.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <AccessTimeRounded color="primary" sx={{ fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                            ساعات کاری واحد پشتیبانی: <strong>شنبه تا چهارشنبه از ساعت ۹:۰۰ الی ۱۷:۰۰</strong>
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <CheckCircleOutlineRounded color="success" sx={{ fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                            پاسخ تیکت‌ها معمولاً در کمتر از ۲ ساعت کاری در همین سامانه درج و پیامک اطلاع‌رسانی ارسال می‌شود.
                        </Typography>
                    </Box>
                </Stack>

                <Divider sx={{ my: 2.5, borderStyle: 'dashed' }} />

                {/* Direct Contact Cards */}
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
                    راه‌های ارتباط مستقیم:
                </Typography>
                <Stack spacing={1.5}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            borderRadius: 2.5,
                            bgcolor: 'action.hover',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <PhoneInTalkRounded color="primary" sx={{ fontSize: 18 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>تماس تلفنی پشتیبانی</Typography>
                        </Box>
                        <Typography
                            component="a"
                            href="tel:02128424190"
                            variant="body2"
                            sx={{ fontWeight: 700, direction: 'rtl', color: 'primary.main', textDecoration: 'none' }}
                        >
                            (۰۲۱)۲۸۴۲۴۱۹۰
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            borderRadius: 2.5,
                            bgcolor: 'action.hover',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <EmailOutlined color="primary" sx={{ fontSize: 18 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>ایمیل امور مشتریان</Typography>
                        </Box>
                        <Typography
                            component="a"
                            href="mailto:support@gympin.ir"
                            variant="body2"
                            sx={{ fontWeight: 600, direction: 'ltr', color: 'text.secondary', textDecoration: 'none' }}
                        >
                            support@gympin.ir
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default _SupportInfo;
